import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import type * as React from 'react';

const BAR_COUNT = 16;

type LevelBar = {
	readonly id: string;
	/** Deterministic per-bar weight so every bar does not move in lockstep with `level`: purely
	 * cosmetic, never read for anything but rendering. */
	readonly weight: number;
};

const LEVEL_BARS: readonly LevelBar[] = Array.from({ length: BAR_COUNT }, (_unused, index) => ({
	id: `level-bar-${index}`,
	weight: 0.35 + ((index * 37) % 65) / 100,
}));
const MIN_HEIGHT_RATIO = 0.12;
const PAUSED_HEIGHT_RATIO = 0.18;

const containerStyle = css`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 5px;
	height: 52px;
	width: 100%;
	max-width: 420px;
`;

const barBaseStyle = css`
	flex: 1;
	min-width: 3px;
	max-width: 6px;
	border-radius: ${theme.radii.pill};
	background-color: ${theme.colors.waveform};
`;

type Props = {
	readonly level: number;
	readonly isPaused: boolean;
};

/**
 * Sixteen bars driven by `CaptureSnapshot.level` (`0..1`). Height stops transitioning and settles low
 * while paused, per `reference/design-direction.md` §5 — nothing here is decorative, the waveform
 * color only ever means "capture in progress".
 */
const RecordingLevelMeter: React.FC<Props> = ({ level, isPaused }) => {
	return (
		<div css={containerStyle}>
			{LEVEL_BARS.map((bar) => {
				const heightRatio = isPaused
					? PAUSED_HEIGHT_RATIO
					: Math.max(MIN_HEIGHT_RATIO, Math.min(1, level * 5 * bar.weight + 0.08));

				return (
					<span
						key={bar.id}
						css={css`
              ${barBaseStyle}
              height: ${Math.round(heightRatio * 100)}%;
              transition: ${isPaused ? 'none' : `height ${theme.motion.duration} ease`};
            `}
					/>
				);
			})}
		</div>
	);
};

export default RecordingLevelMeter;
